"use client";

import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { useUnreadCount } from "@/lib/use-unread";
import { getMe } from "@/lib/auth-store";
import { isActive, ownProfilePathOf } from "./nav";
import { useNavItems } from "@/lib/use-public-settings";

export interface BottomTabBarProps {
  /**
   * Render in normal flow (as the last child of the fit-mode h-dvh flex
   * column) instead of position:fixed. iOS WebKit can anchor fixed elements
   * to a stale, shorter layout viewport on standalone cold launch, leaving a
   * gap under the bar; fit-mode screens never scroll the body so it never
   * self-corrects. In-flow positioning comes from layout and is immune.
   * (The launch-gap root cause itself — the PWA opening letterboxed below
   * the status bar — is fixed by the explicit apple-mobile-web-app-capable
   * meta in the root layout.)
   */
  inFlow?: boolean;
}

/**
 * Bottom navigation for mobile. Active tab gets a flamingo-lit icon,
 * a soft glow halo, and a visible label. Honors the bottom safe-area inset.
 * Hidden from tablet up (desktop uses the left rail). Fixed by default;
 * in-flow on fit-mode screens (see {@link BottomTabBarProps.inFlow}).
 */
export function BottomTabBar({ inFlow = false }: BottomTabBarProps) {
  const navItems = useNavItems();
  const pathname = usePathname() ?? "";
  const unread = useUnreadCount();
  const t = useTranslations("nav");
  const [ownProfilePath, setOwnProfilePath] = useState<string | null>(null);

  useEffect(() => {
    // Identity lives in client-only storage, resolved after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOwnProfilePath(ownProfilePathOf(getMe()?.username));
  }, []);

  return (
    <nav
      aria-label={t("primary")}
      className={cn(
        "md:hidden",
        inFlow ? "shrink-0" : "fixed inset-x-0 bottom-0 z-40",
        "border-t border-white/8 bg-ink/80 backdrop-blur-xl",
        "pb-[env(safe-area-inset-bottom)]",
      )}
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around px-2">
        {navItems.map(({ href, labelKey, Icon }) => {
          const active = isActive(pathname, href, ownProfilePath);
          return (
            <li key={href} className="flex-1">
              <Link
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group relative flex min-h-[3.5rem] flex-col items-center justify-center gap-1 py-2",
                  "text-2xs font-medium transition-colors",
                  active ? "text-flamingo" : "text-white/55 hover:text-white/75",
                )}
              >
                <span className="relative flex items-center justify-center">
                  {active && (
                    <span
                      aria-hidden="true"
                      className="absolute -inset-2 rounded-full bg-flamingo/20 blur-lg"
                    />
                  )}
                  <Icon
                    className={cn(
                      "relative size-6 transition-transform duration-200",
                      active && "scale-105",
                    )}
                  />
                  {href === "/matches" && unread > 0 && (
                    <span className="absolute -right-2 -top-1 flex min-w-4 items-center justify-center rounded-full bg-flamingo px-1 text-3xs font-bold text-white">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </span>
                <span>{t(labelKey)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
